import React, { useState, useEffect } from 'react';
import apiService from '../services/apiService';
import { translations } from '../translations';

function PatientHistoryModal({ patient, onClose, language = 'en' }) {
  const [historyData, setHistoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const t = translations[language] || translations.en;

  useEffect(() => {
    if (patient && patient.patientId) {
      loadPatientHistory();
    }
  }, [patient]);

  const loadPatientHistory = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getPatientHistory(patient.patientId);
      if (response.success) {
        setHistoryData(response);
      } else {
        setError(response.message || 'Erreur lors du chargement de l\'historique');
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
      setError(error.message || 'Erreur lors du chargement de l\'historique');
    } finally {
      setLoading(false);
    }
  };

  const getTaskTypeIcon = (taskType) => {
    const icons = {
      'sample': '🧪',
      'questionnaire': '📋',
      'measurement': '📏',
      'guidance': '💡',
      'input': '✏️',
      'test': '🔬',
      'tips': '💡',
      'support': '🤝'
    };
    return icons[taskType] || '📝';
  };

  const getTaskTypeColor = (taskType) => {
    const colors = {
      'sample': '#ef4444',
      'questionnaire': '#3b82f6',
      'measurement': '#10b981',
      'guidance': '#f59e0b',
      'input': '#8b5cf6',
      'test': '#06b6d4',
      'tips': '#84cc16',
      'support': '#f97316'
    };
    return colors[taskType] || '#6b7280';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString(language === 'de' ? 'de-DE' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculer les statistiques de progression
  const getProgressStats = () => {
    if (!historyData?.timeline || !historyData?.patientInfo?.startDate) {
      return { overallProgress: 0, expectedProgress: 0, completedTasks: 0, totalTasks: 0, expectedTasks: 0 };
    }
    
    const today = new Date();
    const startDate = new Date(historyData.patientInfo.startDate + 'T00:00:00.000Z');
    const daysSinceStart = Math.max(0, Math.floor((today - startDate) / (1000 * 60 * 60 * 24)));
    
    let totalTasks = 0;
    let completedTasks = 0;
    let expectedTasks = 0;
    
    historyData.timeline.forEach(phase => {
      phase.days.forEach(day => {
        day.tasks.forEach(task => {
          totalTasks++;
          if (task.completed) {
            completedTasks++;
          }
          // Une tâche est attendue si le jour est débloqué selon la règle
          if (typeof day.dayNumber === 'number') {
            const UNLOCK_SHIFT = 21;
            const allowableDayNumber = daysSinceStart - UNLOCK_SHIFT;
            if (day.dayNumber <= allowableDayNumber) {
              expectedTasks++;
            }
          }
        });
      });
    });
    
    const overallProgress = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;
    const expectedProgress = totalTasks > 0 ? Math.round((expectedTasks / totalTasks) * 100) : 0;
    
    return { overallProgress, expectedProgress, completedTasks, totalTasks, expectedTasks };
  };

  // Calculer les tâches en retard
  const getOverdueTasks = () => {
    if (!historyData?.timeline || !historyData?.patientInfo?.startDate) return [];
    
    const today = new Date();
    const startDate = new Date(historyData.patientInfo.startDate + 'T00:00:00.000Z');
    const daysSinceStart = Math.max(0, Math.floor((today - startDate) / (1000 * 60 * 60 * 24)));
    
    const overdueTasks = [];
    
    historyData.timeline.forEach(phase => {
      phase.days.forEach(day => {
        // Vérifier si le jour est débloqué et si ses tâches devraient être complétées
        const UNLOCK_SHIFT = 21;
        const allowableDayNumber = daysSinceStart - UNLOCK_SHIFT;
        const shouldCheckForOverdue = day.unlocked && day.dayNumber <= allowableDayNumber;
        
        if (shouldCheckForOverdue) {
          day.tasks.forEach(task => {
            if (!task.completed) {
              // Calculer correctement les jours de retard
              const daysSinceUnlock = Math.max(0, allowableDayNumber - day.dayNumber);
              const daysOverdue = Math.max(0, daysSinceUnlock);
              
              overdueTasks.push({
                ...task,
                phaseId: phase.phaseId,
                dayNumber: day.dayNumber,
                daysOverdue: daysOverdue
              });
            }
          });
        }
      });
    });
    
    return overdueTasks.sort((a, b) => b.daysOverdue - a.daysOverdue);
  };

  // Fonction pour déterminer la couleur selon le niveau de retard
  const getOverdueColor = (daysOverdue) => {
    if (daysOverdue < 2) return '#fbbf24'; // Jaune pour < 2 jours
    if (daysOverdue < 5) return '#f59e0b'; // Orange pour 2-4 jours
    return '#dc2626'; // Rouge pour >= 5 jours
  };

  if (loading) {
    return (
      <div className="modal-overlay">
        <div className="modal-content simple-modal">
          <div className="modal-header">
            <h2>📊 {language === 'de' ? 'Patientenhistorie' : 'Patient History'}</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="loading-spinner">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay">
        <div className="modal-content simple-modal">
          <div className="modal-header">
            <h2>📊 {language === 'de' ? 'Patientenhistorie' : 'Patient History'}</h2>
            <button className="modal-close" onClick={onClose}>×</button>
          </div>
          <div className="modal-body">
            <div className="error-message">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  const overdueTasks = getOverdueTasks();
  const progressStats = getProgressStats();

  return (
    <div className="modal-overlay">
      <div className="modal-content simple-modal">
        <div className="modal-header">
          <h2>📊 {language === 'de' ? 'Patientenhistorie' : 'Patient History'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Informations patient */}
          <div className="patient-info-section">
            <h3>👤 {language === 'de' ? 'Patient' : 'Patient'}</h3>
            <div className="patient-details">
              <p><strong>ID:</strong> {patient.patientId}</p>
              <p><strong>{language === 'de' ? 'Startdatum' : 'Start Date'}:</strong> {formatDate(historyData?.patientInfo?.startDate)}</p>
              <p><strong>{language === 'de' ? 'Sprache' : 'Language'}:</strong> {patient.language || 'en'}</p>
            </div>
          </div>

          {/* Cercle de progression à double jauge */}
          <div className="progress-section">
            <h3>📈 {language === 'de' ? 'Fortschritt' : 'Progress'}</h3>
            <div className="progress-circle-container">
              <div className="progress-circle-wrapper">
                {/* Pourcentage réel à gauche */}
                <div className="progress-percentage-left">
                  <span className="progress-number">{progressStats.overallProgress}%</span>
                </div>
                
                {/* Cercle double jauge */}
                <div className="progress-circle-large">
                  <svg width="120" height="120" viewBox="0 0 120 120">
                    {/* Cercle de fond */}
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="#e5e7eb"
                      strokeWidth="8"
                    />
                    {/* Jauge attendue (arrière-plan, bleu clair) */}
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="#93c5fd"
                      strokeWidth="8"
                      strokeDasharray={`${2 * Math.PI * 50}`}
                      strokeDashoffset={`${2 * Math.PI * 50 * (1 - progressStats.expectedProgress / 100)}`}
                      transform="rotate(-90 60 60)"
                      style={{ transition: 'stroke-dashoffset 1s' }}
                    />
                    {/* Jauge réelle (avant-plan, bleu vif) */}
                    <circle
                      cx="60"
                      cy="60"
                      r="50"
                      fill="none"
                      stroke="#3b82f6"
                      strokeWidth="8"
                      strokeDasharray={`${2 * Math.PI * 50}`}
                      strokeDashoffset={`${2 * Math.PI * 50 * (1 - progressStats.overallProgress / 100)}`}
                      transform="rotate(-90 60 60)"
                      style={{ transition: 'stroke-dashoffset 1s' }}
                    />
                  </svg>
                  
                  {/* Texte centré sous le cercle */}
                  <div className="progress-text-center">
                    {progressStats.overallProgress === progressStats.expectedProgress ? (
                      <span className="progress-status up-to-date">
                        {language === 'de' ? 'Aktuell' : 'Up to date'}
                      </span>
                    ) : (
                      <span className="progress-status pending">
                        {progressStats.expectedTasks - progressStats.completedTasks} {language === 'de' ? 'Aufgaben ausstehend' : 'tasks pending'}
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Pourcentage attendu à droite */}
                <div className="progress-percentage-right">
                  {progressStats.overallProgress === progressStats.expectedProgress ? (
                    <span className="progress-number expected">
                      {progressStats.expectedProgress === 100 ? '' : progressStats.expectedProgress + '%'}
                    </span>
                  ) : (
                    <span className="progress-status pending-text">
                      {language === 'de' ? 'Ausstehend' : 'Pending'}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Légende des jauges */}
              <div className="progress-legend">
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: '#3b82f6' }}></div>
                  <span>{progressStats.completedTasks}/{progressStats.totalTasks} {language === 'de' ? 'Aufgaben erledigt' : 'tasks completed'}</span>
                </div>
                <div className="legend-item">
                  <div className="legend-color" style={{ backgroundColor: '#93c5fd' }}></div>
                  <span>{progressStats.expectedTasks - progressStats.completedTasks}/{progressStats.totalTasks} {language === 'de' ? 'Aufgaben ausstehend' : 'tasks pending'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tâches en retard */}
          <div className="overdue-section">
            <h3>⚠️ {language === 'de' ? 'Aufgaben im Rückstand' : 'Overdue Tasks'}</h3>
            {overdueTasks.length > 0 ? (
              <div className="overdue-list-simple">
                {overdueTasks.map((task, index) => (
                  <div key={`${task.taskId}-${index}`} className="overdue-item-simple">
                    <div className="overdue-item-content">
                      <span className="task-icon-small" style={{ color: getTaskTypeColor(task.taskType) }}>
                        {getTaskTypeIcon(task.taskType)}
                      </span>
                      <span className="task-title-simple">{task.title}</span>
                      <span className="task-location-simple">
                        P{task.phaseId}•D{task.dayNumber}
                      </span>
                      <span 
                        className="overdue-days-simple"
                        style={{ 
                          backgroundColor: getOverdueColor(task.daysOverdue),
                          color: task.daysOverdue < 2 ? '#92400e' : 'white'
                        }}
                      >
                        {task.daysOverdue}d
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-overdue">
                <p>✅ {language === 'de' ? 'Keine Aufgaben im Rückstand' : 'No overdue tasks'}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PatientHistoryModal; 